// Unit types as data: the traits, the standard units and squads, zones, labels, and the points
// formula (the team editor shows it; nothing in the rules uses it).

import { Hex, DIRECTIONS } from './board';
import type { CtfState, Team, TraitId, Unit, UnitType } from './types';

// Every trait: its name and rules text, and whether it helps or hinders the unit (for the editor).
export const TRAITS: Record<TraitId, { name: string; text: string; up: boolean }> = {
  heavy: { name: 'Heavy', text: 'Counts as 2 units in a cell (for claims and holding) and when assisting.', up: true },
  sentinel: { name: 'Sentinel', text: 'Its zone stops enemies all round, even after it acts.', up: true },
  sureStrike: { name: 'Sure strike', text: 'Its tags need 1 less.', up: true },
  tough: { name: 'Tough', text: 'Tags against it need 1 more.', up: true },
  slippery: { name: 'Slippery', text: "Enemy zones don't stop it.", up: true },
  flagCarrier: { name: 'Flag carrier', text: 'The flag weight costs it 1 less Move.', up: true },
  reverse: { name: 'Reverse', text: 'Can step straight backward without turning, for 2 Move.', up: true },
  noFlag: { name: 'No flag', text: "Can't carry the flag.", up: false },
  noClaim: { name: 'No claim', text: "Can't claim a cell itself, but still counts toward its team's units in a cell.", up: false },
  noGuardZone: { name: 'Open zone', text: 'Its zone never stops enemies (it still tags and assists there).', up: false },
  clumsy: { name: 'Clumsy', text: 'Its tags need 1 more.', up: false },
  fragile: { name: 'Fragile', text: 'Tags against it need 1 less.', up: false },
  slowTurns: { name: 'Slow turns', text: 'Each turn costs 2 Move.', up: false },
  flagHeavy: { name: 'Flag-heavy', text: 'Carries the flag at 1 less Move.', up: false },
};
export const TRAIT_IDS = Object.keys(TRAITS) as TraitId[];
// Traits that can't go on the same unit (they contradict each other or one makes the other meaningless).
export const TRAIT_CLASHES: [TraitId, TraitId][] = [
  ['sureStrike', 'clumsy'], ['tough', 'fragile'], ['sentinel', 'noGuardZone'], ['noFlag', 'flagHeavy'],
  ['noFlag', 'flagCarrier'], ['flagHeavy', 'flagCarrier'],
];

// Zones: offsets from the unit facing direction 0 (SE). One turn (facing + 1) maps each direction to
// the next, which in axial coordinates is (q, r) → (q + r, −q).
const turnOnce = ([q, r]: [number, number]): [number, number] => [q + r, -q];
export function turnOffset(o: [number, number], facing: number): [number, number] {
  let x = o;
  for (let i = 0; i < ((facing % 6) + 6) % 6; i++) x = turnOnce(x);
  return x;
}
// Zone offsets turned to each facing, cached per unit type (types are shared by every state of a game).
const turned = new WeakMap<UnitType, [number, number][][]>();
function zoneByFacing(type: UnitType): [number, number][][] {
  let t = turned.get(type);
  if (!t) { t = [0, 1, 2, 3, 4, 5].map(f => type.zone.map(o => turnOffset(o, f))); turned.set(type, t); }
  return t;
}
export function zoneAt(type: UnitType, pos: Hex, facing: number): Hex[] {
  return zoneByFacing(type)[((facing % 6) + 6) % 6].map(([q, r]) => ({ q: pos.q + q, r: pos.r + r }));
}
const D = (i: number): [number, number] => [DIRECTIONS[i].q, DIRECTIONS[i].r];

// The standard units.
export const SQUIRE: UnitType = { name: 'Squire', letter: '', move: 4, zone: [D(5), D(0), D(1)], traits: [] };
export const LANCER: UnitType = { name: 'Lancer', letter: 'L', move: 7, zone: [D(0), [2, 0]], traits: [] };
export const KNIGHT: UnitType = { name: 'Knight', letter: 'K', move: 6, zone: [0, 1, 2, 3, 4, 5].map(D), traits: ['heavy', 'noFlag', 'noClaim'] };

// The built-in teams, offered in the game settings and the team editor: a goalkeeper (slot 1, in goal),
// three generalists and a striker (October 2026, designed in the team editor; the earlier Tasting Menu,
// Magic Class and Chess Team were retired). Three squires
// is the default (October 2026: the Knight became the Bulwark, Move 6 → 4, and the Lancer Move 8 → 7; the
// extra Move broke the game).
// October 2026 (first league playtest): Raider gains No claim; Warden gains a zone hex 2 ahead; Lieutenant
// loses Heavy and gains Sure strike and No guard zone (since renamed Open zone); Peasant Move 6 → 5; "Warden, 3 Pikeman, Rogue"
// renamed Jailhouse Rockers; Warden and Pikemen gain Reverse.
export const TEAMS: Record<string, { name: string; squad: UnitType[] }> = {
  threeSquires: {
    name: 'Three squires',
    squad: [
      { name: 'Bulwark', letter: 'B', move: 4, zone: [[1,0],[1,-1],[0,-1],[-1,0],[-1,1],[0,1]], traits: ['heavy','tough','noFlag','noClaim'] },
      { name: 'Squire', letter: 'S', move: 4, zone: [[0,1],[1,0],[1,-1]], traits: [] },
      { name: 'Squire', letter: 'S', move: 4, zone: [[0,1],[1,0],[1,-1]], traits: [] },
      { name: 'Squire', letter: 'S', move: 4, zone: [[0,1],[1,0],[1,-1]], traits: [] },
      { name: 'Lancer', letter: 'L', move: 7, zone: [[1,0],[2,0]], traits: [] },
    ],
  },
  townDefense: {
    name: "Town Defense",
    squad: [
      { name: "Swordsman", letter: "S", move: 5, zone: [[0,1],[1,0],[1,-1]], traits: ['sureStrike','slippery','noFlag','noClaim'] },
      { name: "Militia", letter: "M", move: 4, zone: [[0,1],[1,0],[-1,1]], traits: [] },
      { name: "Militia", letter: "M", move: 4, zone: [[0,1],[1,0],[-1,1]], traits: [] },
      { name: "Militia", letter: "M", move: 4, zone: [[0,1],[1,0],[-1,1]], traits: [] },
      { name: "Raider", letter: "R", move: 8, zone: [[1,0],[1,-1],[0,1],[-1,1]], traits: ['heavy','slowTurns','noClaim'] },
    ],
  },
  merryMen: {
    name: "Merry Men",
    squad: [
      { name: "Stick-Fighter", letter: "SF", move: 5, zone: [[1,0],[1,-1],[0,1],[0,-1],[-1,1],[-1,0]], traits: ['sentinel','slippery','noFlag','noClaim'] },
      { name: "Archer", letter: "A", move: 4, zone: [[2,0],[2,-1],[1,1]], traits: [] },
      { name: "Archer", letter: "A", move: 4, zone: [[2,0],[2,-1],[1,1]], traits: [] },
      { name: "Archer", letter: "A", move: 4, zone: [[2,0],[2,-1],[1,1]], traits: [] },
      { name: "Sniper", letter: "S", move: 6, zone: [[3,0],[2,1],[3,-1],[1,1],[2,0],[2,-1]], traits: ['noGuardZone'] },
    ],
  },
  tastingMenu: {
    name: "Tasting Menu",
    squad: [
      { name: "Mage", letter: "M", move: 4, zone: [[3,-2],[3,-1],[3,0],[1,2],[2,1]], traits: ['noFlag'] },
      { name: "Peasant", letter: "P", move: 5, zone: [[1,0]], traits: [] },
      { name: "Peasant", letter: "P", move: 5, zone: [[1,0]], traits: [] },
      { name: "Peasant", letter: "P", move: 5, zone: [[1,0]], traits: [] },
      { name: "Cavalier", letter: "C", move: 8, zone: [[1,0]], traits: ['sureStrike','slowTurns'] },
    ],
  },
  assaultSquad: {
    name: "Assault Squad",
    squad: [
      { name: "Lieutenant", letter: "L", move: 5, zone: [[1,0],[1,-1],[0,1],[0,-1]], traits: ['sureStrike','noGuardZone','noFlag'] },
      { name: "Guard", letter: "G", move: 4, zone: [[1,0],[1,-1],[0,-1]], traits: [] },
      { name: "Guard", letter: "G", move: 4, zone: [[1,0],[1,-1],[0,-1]], traits: [] },
      { name: "Guard", letter: "G", move: 4, zone: [[1,0],[1,-1],[0,-1]], traits: [] },
      { name: "Crossbowman", letter: "C", move: 5, zone: [[1,0],[2,0],[3,-1],[3,0],[2,1]], traits: ['sureStrike'] },
    ],
  },
  warden: {
    name: "Jailhouse Rockers",
    squad: [
      { name: "Warden", letter: "W", move: 3, zone: [[1,0],[2,0],[1,-1],[0,1],[0,-1],[-1,1]], traits: ['sentinel','tough','noFlag','reverse'] },
      { name: "Pikeman", letter: "P", move: 4, zone: [[1,0],[2,0]], traits: ['reverse'] },
      { name: "Pikeman", letter: "P", move: 4, zone: [[1,0],[2,0]], traits: ['reverse'] },
      { name: "Pikeman", letter: "P", move: 4, zone: [[1,0],[2,0]], traits: ['reverse'] },
      { name: "Rogue", letter: "R", move: 6, zone: [[1,0],[-1,1],[-1,0],[0,-1]], traits: ['slippery'] },
    ],
  },
};
export const DEFAULT_TEAM = 'threeSquires';


// The old standard squads, kept as fixtures for the rules tests (squires2: the 4.x default).
export const SQUADS: Record<string, UnitType[]> = {
  squires2: [KNIGHT, SQUIRE, SQUIRE, LANCER, LANCER],
  squires3: [KNIGHT, SQUIRE, SQUIRE, LANCER, SQUIRE],
};
export const SQUAD_SIZE = 5;

export const typeOf = (s: CtfState, u: Unit): UnitType => s.config.squads[u.team][u.slot];
export const hasTrait = (s: CtfState, u: Unit, t: TraitId) => typeOf(s, u).traits.includes(t);

// Unit ids: team, type name in lower case, and a number when the squad has more than one of it.
export const slug = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '') || 'unit';
export function unitIds(team: Team, squad: UnitType[]): string[] {
  return squad.map((t, i) => {
    const same = squad.filter(x => slug(x.name) === slug(t.name));
    const n = squad.slice(0, i + 1).filter(x => slug(x.name) === slug(t.name)).length;
    return `${team}-${slug(t.name)}${same.length > 1 ? n : ''}`;
  });
}

// Board label: the type's letter, numbered when the squad has more than one of it; a blank letter
// (squires) is numbered only ("1", "2", "L1", "L2", "K").
export function slotLabel(squad: UnitType[], slot: number): string {
  const t = squad[slot];
  const same = squad.map((x, i) => ({ x, i })).filter(({ x }) => x.letter === t.letter && slug(x.name) === slug(t.name));
  const n = same.findIndex(({ i }) => i === slot) + 1;
  if (!t.letter) return String(n);
  return same.length > 1 ? `${t.letter}${n}` : t.letter;
}
export const unitLabel = (s: CtfState, u: Unit) => slotLabel(s.config.squads[u.team], u.slot);

// ── Points ───────────────────────────────────────────────────────────────────
// A first guess, to be tuned (by hand and by simulation): a base, Move, each zone hex by its distance
// and direction, and each trait. Only an indicator: nothing enforces a budget.

export interface PointsTable {
  base: number;
  perMove: number;
  hex: Record<1 | 2 | 3, { front: number; side: number; back: number }>;
  traits: Record<TraitId, number>;
  budget: number;
}
export const DEFAULT_POINTS: PointsTable = {
  base: 40,
  perMove: 20,
  hex: { 1: { front: 20, side: 15, back: 10 }, 2: { front: 30, side: 25, back: 20 }, 3: { front: 40, side: 35, back: 30 } },
  traits: {
    heavy: 40, sentinel: 40, sureStrike: 30, tough: 30, slippery: 30, flagCarrier: 10, reverse: 20,
    noFlag: -40, noClaim: -30, noGuardZone: -30, clumsy: -30, fragile: -30, slowTurns: -20, flagHeavy: -10,
  },
  budget: 1000,                      // the standard squad (knight, 2 squires, 2 lancers) costs 1010
};

const hexDistance = (q: number, r: number) => (Math.abs(q) + Math.abs(r) + Math.abs(q + r)) / 2;
// Where a zone offset lies relative to facing 0: in front, to the side or behind (by the angle to it).
export function hexSector([q, r]: [number, number]): 'front' | 'side' | 'back' {
  const x = Math.sqrt(3) * (q + r / 2), y = 1.5 * r;            // pixel position, facing 0 points along DIRECTIONS[0]
  const fx = Math.sqrt(3) * 1, fy = 0;
  const cos = (x * fx + y * fy) / (Math.hypot(x, y) * Math.hypot(fx, fy));
  return cos > 0.85 ? 'front' : cos > -0.05 ? 'side' : 'back';   // front: within 30°; side: up to 90°
}
export function hexPrice(o: [number, number], table: PointsTable = DEFAULT_POINTS): number {
  const d = Math.min(3, Math.max(1, hexDistance(o[0], o[1]))) as 1 | 2 | 3;
  return table.hex[d][hexSector(o)];
}
export function unitCost(t: UnitType, table: PointsTable = DEFAULT_POINTS): number {
  const zone = t.zone.reduce((v, o) => v + hexPrice(o, table), 0);
  const traits = t.traits.reduce((v, id) => v + (table.traits[id] ?? 0), 0);
  return Math.round(table.base + table.perMove * t.move + zone + traits);
}
export const squadCost = (squad: UnitType[], table: PointsTable = DEFAULT_POINTS) =>
  squad.reduce((v, t) => v + unitCost(t, table), 0);

// ── Text ─────────────────────────────────────────────────────────────────────

const sameZone = (a: [number, number][], b: [number, number][]) =>
  a.length === b.length && a.every(([q, r]) => b.some(([x, y]) => x === q && y === r));
// A zone in words: the standard shapes by name, others by size and reach.
export function zoneText(t: UnitType): string {
  if (!t.zone.length) return 'no hexes';
  if (sameZone(t.zone, SQUIRE.zone)) return 'the 3 hexes in front';
  if (sameZone(t.zone, LANCER.zone)) return 'a lane 2 hexes straight ahead';
  if (sameZone(t.zone, KNIGHT.zone)) return 'all 6 hexes around it';
  const reach = Math.max(...t.zone.map(([q, r]) => hexDistance(q, r)));
  return `${t.zone.length} hex${t.zone.length === 1 ? '' : 'es'}, up to ${reach} away`;
}
// One unit type in words: Move, zone and traits.
export function unitText(t: UnitType): string {
  return [`Move ${t.move}`, `zone: ${zoneText(t)}`, ...t.traits.map(id => TRAITS[id].name.toLowerCase())].join(', ');
}
